// Story 11.3 — TanStack Query mutation for
// POST /api/v1/admin/vat-registration.
//
// ⛔ THE ONLY WAY THE PLATFORM'S VAT REGISTRATION STATE EVER CHANGES. Not an
// environment variable, not a config file, not a deploy — an authenticated
// admin action that writes an audited, append-only row with an effective date.
//
// Append-only means there is no PATCH and no DELETE here, and there never will
// be: a row with a wrong effective date is corrected by recording ANOTHER row
// with the same effective date, which supersedes it in the in-force read while
// leaving the original visible in the log. `note` is where that correction says
// why.
//
// Fresh Idempotency-Key per submit (crypto.randomUUID), matching the admin
// block/unblock precedent — the endpoint is covered by the idempotency
// middleware, so a network retry replays the cached response instead of leaving
// a duplicate state row behind.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RecordVatRegistrationStateInput = {
  state: "not_registered" | "registered";
  /** "YYYY-MM-DD". May be past (confirmed late) or future (a planned flip). */
  effective_from: string;
  /** ⛔ Must be null for `not_registered` — an unregistered seller has none. */
  vat_registration_number: string | null;
  /** "YYYY-MM-DD", or null while supplier-state placement still applies. */
  eu_destination_vat_from: string | null;
  note: string | null;
};

export type RecordVatRegistrationStateResponse = {
  data: {
    id: string;
    audit_log_id: string;
    state: string;
    effective_from: string;
    vat_registration_number: string | null;
    eu_destination_vat_from: string | null;
    recorded_at: string;
  };
};

export type RecordVatRegistrationStateError = {
  code: string;
  message: string;
  field?: string;
  status: number;
};

async function recordState(
  input: RecordVatRegistrationStateInput
): Promise<RecordVatRegistrationStateResponse> {
  const response = await fetch("/api/v1/admin/vat-registration", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": crypto.randomUUID(),
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RecordVatRegistrationStateError = {
      code: "ADMIN_VAT_REGISTRATION_FAILED",
      message: `Recording the registration state failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as RecordVatRegistrationStateResponse;
}

export function useRecordVatRegistrationState() {
  const queryClient = useQueryClient();
  return useMutation<
    RecordVatRegistrationStateResponse,
    RecordVatRegistrationStateError,
    RecordVatRegistrationStateInput
  >({
    mutationFn: recordState,
    onSuccess: () => {
      // The current state is served by the metrics dashboard's
      // `vat_registration` panel — there is deliberately no second GET for the
      // same fact, so this is the query that has to be refreshed.
      queryClient.invalidateQueries({ queryKey: ["admin-metrics"] });
    },
  });
}
